import { AutocompleteGenericEntity } from '@australiangreens/ag-internal-components';
import { Search as ElectionsPopupIcon } from '@mui/icons-material';
import { Box, Checkbox, FormControlLabel, InputLabel } from '@mui/material';
import {
  BaseWidget,
  BaseWidgetProps,
  Config,
  FactoryWithContext,
} from '@react-awesome-query-builder/mui';

import {
  arePermissibleTaskChoicesAndKinds,
  isTemplatePlaceholder,
  TASK_CHOICE_MAPPING_REVERSE,
  TASK_KIND_MAPPING_REVERSE,
  TaskChoice,
  TaskKind,
  TemplatePlaceholder,
} from '@/types';
import { SearchTemplatePageContext } from '@/context/SearchTemplateContext';
import {
  TASK_CHOICE_AUTOCOMPLETE_OPTIONS,
  TASK_KIND_AUTOCOMPLETE_OPTIONS,
} from '@/constants/filterCriteria/tasks';
import { useContext } from 'react';
import MissingFilterValueWarning from './MissingFilterValueWarning';
import TemplateCapableFetchAutocomplete from '@/components/TemplateCapableFetchAutocomplete';
import TemplateCapableTextField from './templateCapableComponents/TemplateCapableTextField';

export {
  TASK_CHOICE_AUTOCOMPLETE_OPTIONS,
  TASK_KIND_AUTOCOMPLETE_OPTIONS,
} from '@/constants/filterCriteria/tasks';

const TASK_NOTE_MAX_LENGTH = 120;

// This gets mapped to a TaskFilter
export type TaskWidgetValue = {
  elections: AutocompleteGenericEntity[] | TemplatePlaceholder;
  isSinceSpecificElection: boolean;
  kinds: AutocompleteGenericEntity[] | TemplatePlaceholder;
  choices: AutocompleteGenericEntity[] | TemplatePlaceholder;
  note: string | TemplatePlaceholder;

  // Errors is checked by searchTreeHasIncompleteFilter() (via
  // isJsonTreeIncomplete()). At time of writing the contents are not checked,
  // only its length.
  errors: string[];
};

type TaskWidgetProps = {
  lookupElections: (lookupValue: string) => Promise<AutocompleteGenericEntity[]>;
};

type CombinedProps = BaseWidgetProps<Config, TaskWidgetValue> & TaskWidgetProps;

const kindsAndChoicesArePermissible = (
  kinds: AutocompleteGenericEntity[],
  choices: AutocompleteGenericEntity[]
) => {
  const taskKindCodes = kinds.map((x) => TASK_KIND_MAPPING_REVERSE[x.label]);
  const taskChoiceCodes = choices.map((x) => TASK_CHOICE_MAPPING_REVERSE[x.label]);

  return arePermissibleTaskChoicesAndKinds(
    taskChoiceCodes as TaskChoice[],
    taskKindCodes as TaskKind[]
  );
};

const labelStyles = { margin: 0 };

const INITIAL_VALUE: TaskWidgetValue = {
  elections: [],
  isSinceSpecificElection: false,
  kinds: [],
  choices: [
    TASK_CHOICE_AUTOCOMPLETE_OPTIONS.find((x) => x.id === 'Y') as AutocompleteGenericEntity,
    TASK_CHOICE_AUTOCOMPLETE_OPTIONS.find((x) => x.id === 'E') as AutocompleteGenericEntity,
  ],
  errors: [],
  note: '',
};

export const TaskWidgetFactory: FactoryWithContext<CombinedProps> = ({
  value,
  setValue,
  lookupElections,
}: CombinedProps) => {
  const { elections, isSinceSpecificElection, kinds, choices, note } = value ?? INITIAL_VALUE;
  const inSearchTemplate = useContext(SearchTemplatePageContext);

  const excessiveElections =
    isSinceSpecificElection && !isTemplatePlaceholder(elections) && elections.length > 1;
  const isTaskChoiceError =
    !isTemplatePlaceholder(kinds) &&
    !isTemplatePlaceholder(choices) &&
    !kindsAndChoicesArePermissible(kinds, choices);
  const taskStatusHelperText = isTaskChoiceError
    ? 'Task status invalid for selected task type'
    : '';

  const updateValueField = (field: keyof TaskWidgetValue, newValueFieldValue: unknown) => {
    // setValue does not support updater functions like useState hook setter
    // function does, so we have to reference the old value directly.

    const newValue =
      // choices default values are not blank, so if we change from placeholder to not-placeholder,
      // we should go back to the default values.

      field === 'choices' &&
      isTemplatePlaceholder(value?.choices) &&
      !isTemplatePlaceholder(newValueFieldValue)
        ? {
            ...(value ?? INITIAL_VALUE),
            ['choices']: INITIAL_VALUE.choices,
          }
        : {
            ...(value ?? INITIAL_VALUE),
            [field]: newValueFieldValue,
          };

    setValue({ ...newValue, errors: [] });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        rowGap: "24px"
      }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          rowGap: "4px"
        }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isSinceSpecificElection}
              onChange={(e) => updateValueField('isSinceSpecificElection', e.target.checked)}
            />
          }
          label="Since specific election"
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            width: "100%"
          }}>
          <MissingFilterValueWarning isMissing={(value?.errors.length ?? 0) > 0} />
          <Box sx={{
            flexGrow: 1
          }}>
            <TemplateCapableFetchAutocomplete
              sx={{ width: '50ch' }}
              lookup={lookupElections}
              minLength={3}
              label="Add an election"
              value={elections}
              onChange={(newVal) => updateValueField('elections', newVal)}
              data-testid="Add an election"
              loadingText="Looking for elections..."
              noOptionsText="No elections found"
              popupIcon={<ElectionsPopupIcon />}
              disableIconFlip
              error={excessiveElections || (value?.errors.length ?? 0) > 0}
              helperText={
                excessiveElections
                  ? 'Please select a single election or uncheck the since specific election box'
                  : ''
              }
              isOnTemplatePage={inSearchTemplate}
              templateBuilderDisplayName="Campaign tasks filter: Election"
            />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          rowGap: "24px"
        }}>
        <InputLabel sx={{ color: 'black', ...labelStyles }}>
          {!isTemplatePlaceholder(elections) && elections.length
            ? 'Task options for selected election(s)'
            : 'Select at least one election to see more task options'}
        </InputLabel>

        {(isTemplatePlaceholder(elections) || elections.length !== 0) && (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                rowGap: "4px"
              }}>
              <InputLabel sx={{ color: 'black', ...labelStyles }}>
                {isTemplatePlaceholder(kinds) || kinds.length ? 'Task' : 'Task: Any'}
              </InputLabel>
              <TemplateCapableFetchAutocomplete
                minLength={0}
                label="Add a task"
                preLoadedOptions={TASK_KIND_AUTOCOMPLETE_OPTIONS}
                sx={{ width: '50ch' }}
                value={kinds}
                onChange={(newVal) => updateValueField('kinds', newVal)}
                data-testid="taskkinds"
                loadingText="Looking for tasks..."
                noOptionsText="No tasks found"
                isOnTemplatePage={inSearchTemplate}
                templateBuilderDisplayName="Campaign tasks filter: Task"
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                rowGap: "4px"
              }}>
              <InputLabel sx={{ color: 'black', ...labelStyles }}>
                {isTemplatePlaceholder(choices) || choices.length ? 'Status' : 'Status: Any'}
              </InputLabel>
              <TemplateCapableFetchAutocomplete
                sx={{ width: '50ch' }}
                minLength={0}
                label="Add a status"
                preLoadedOptions={TASK_CHOICE_AUTOCOMPLETE_OPTIONS}
                value={choices}
                onChange={(newVal) => updateValueField('choices', newVal)}
                data-testid="campaignstatuses"
                loadingText="Looking for statuses..."
                noOptionsText="No statuses found"
                error={isTaskChoiceError}
                helperText={taskStatusHelperText}
                isOnTemplatePage={inSearchTemplate}
                templateBuilderDisplayName="Campaign tasks filter: Status"
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                rowGap: "4px"
              }}>
              <InputLabel sx={{ color: 'black', ...labelStyles }}>Task note (optional)</InputLabel>
              <TemplateCapableTextField
                id="tasknote"
                label="Enter text to search for"
                variant="filled"
                fullWidth
                // Component handles valueIsPlaceholder and placeholder styling internally
                value={note ?? ''}
                // Component provides 'reason' but we only need newVal here
                onChange={(newVal) => updateValueField('note', newVal)}
                isOnTemplatePage={inSearchTemplate}
                templateBuilderDisplayName="Campaign tasks filter: Task note"
                sx={{ maxWidth: '389px !important' }}
                slotProps={{
                  htmlInput: {
                    maxLength: TASK_NOTE_MAX_LENGTH,
                  },
                }}
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export const taskWidget: BaseWidget & TaskWidgetProps = {
  type: 'task',
  hideOperator: true,
  operatorInlineLabel: ' ',
  factory: TaskWidgetFactory,

  // Workaround to have it as a 'required' prop despite the way the widget
  // typing works
  lookupElections: () => {
    throw new Error('lookupElections prop must be specified');
  },
};
